import { useCallback, useRef, useState } from 'react'

import helpers from 'helpers'

import useSnackbar from 'hooks/useSnackbar'

import constants from 'constants/index'
import * as service from 'service'

const useKanbanDragDrop = (
  columns,
  setColumns,
  columnStates,
  setColumnStates,
  boardId,
  canFinalize,
) => {
  const { KANBAN_COLUMN_ROLE_FINALIZED } = constants.activities

  const snackbar = useSnackbar()

  const columnStatesRef = useRef(columnStates)
  columnStatesRef.current = columnStates

  const [pendingMove, setPendingMove] = useState(null)

  const emptyColumnState = () => ({
    tasks: [],
    page: 1,
    totalCount: 0,
    loading: true,
  })

  const commitMove = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result

      const sourceTasks =
        columnStatesRef.current[source.droppableId]?.tasks ?? []
      const draggedTask = sourceTasks.find((t) => String(t.id) === draggableId)
      if (!draggedTask) return

      const prevColumnStates = columnStatesRef.current
      const isSameColumn = source.droppableId === destination.droppableId
      const newSourceTasks = sourceTasks.filter(
        (t) => String(t.id) !== draggableId,
      )

      const destTasksWithoutDragged = (
        isSameColumn
          ? sourceTasks
          : columnStatesRef.current[destination.droppableId]?.tasks ?? []
      ).filter((t) => String(t.id) !== draggableId)

      const updatedTask = {
        ...draggedTask,
        kanbanColumnId: Number(destination.droppableId),
        autoManaged: isSameColumn ? draggedTask.autoManaged : false,
      }
      const newDestTasks = [...destTasksWithoutDragged]
      newDestTasks.splice(destination.index, 0, updatedTask)

      setColumnStates((prev) => {
        if (isSameColumn) {
          return {
            ...prev,
            [source.droppableId]: {
              ...(prev[source.droppableId] ?? emptyColumnState()),
              tasks: newDestTasks,
            },
          }
        }
        return {
          ...prev,
          [source.droppableId]: {
            ...(prev[source.droppableId] ?? emptyColumnState()),
            tasks: newSourceTasks,
            totalCount: Math.max(
              0,
              (prev[source.droppableId]?.totalCount ?? 1) - 1,
            ),
          },
          [destination.droppableId]: {
            ...(prev[destination.droppableId] ?? emptyColumnState()),
            tasks: newDestTasks,
            totalCount: (prev[destination.droppableId]?.totalCount ?? 0) + 1,
          },
        }
      })

      try {
        await service.dponet.kanbanTasks.move({
          taskId: draggedTask.id,
          kanbanColumnId: Number(destination.droppableId),
          position: destination.index,
        })
      } catch (error) {
        setColumnStates((prev) => ({
          ...prev,
          [source.droppableId]: prevColumnStates[source.droppableId],
          ...(isSameColumn
            ? {}
            : {
                [destination.droppableId]:
                  prevColumnStates[destination.droppableId],
              }),
        }))
        snackbar.open({
          message: helpers.formatters.errorMessage(error),
          variant: 'error',
        })
      }
    },
    [setColumnStates, snackbar],
  )

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId, type } = result

      if (!destination) return
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      )
        return

      if (type === 'COLUMN') {
        if (columns[source.index]?.role === KANBAN_COLUMN_ROLE_FINALIZED) return

        const maxDest =
          columns.findIndex((c) => c.role === KANBAN_COLUMN_ROLE_FINALIZED) - 1
        const clampedDest =
          maxDest >= 0
            ? Math.min(destination.index, maxDest)
            : destination.index

        const reordered = Array.from(columns)
        const [moved] = reordered.splice(source.index, 1)
        reordered.splice(clampedDest, 0, moved)

        const finalizedIdx = reordered.findIndex(
          (c) => c.role === KANBAN_COLUMN_ROLE_FINALIZED,
        )
        if (finalizedIdx !== -1 && finalizedIdx !== reordered.length - 1) {
          const [fin] = reordered.splice(finalizedIdx, 1)
          reordered.push(fin)
        }

        setColumns(reordered)
        try {
          await service.dponet.kanbanColumns.reorder({
            kanbanBoardId: boardId,
            ids: reordered.map((c) => c.id),
          })
        } catch (error) {
          setColumns(columns)
          snackbar.open({
            message: helpers.formatters.errorMessage(error),
            variant: 'error',
          })
        }
        return
      }

      const destColumn = columns.find(
        (c) => String(c.id) === destination.droppableId,
      )
      if (destColumn?.role === KANBAN_COLUMN_ROLE_FINALIZED && !canFinalize) {
        snackbar.open({
          message: 'Você não tem permissão para finalizar atividades.',
          variant: 'error',
        })
        return
      }

      const sourceTasks =
        columnStatesRef.current[source.droppableId]?.tasks ?? []
      const draggedTask = sourceTasks.find((t) => String(t.id) === draggableId)
      if (!draggedTask) return

      const isSameColumn = source.droppableId === destination.droppableId
      if (draggedTask.autoManaged && !isSameColumn) {
        setPendingMove({ result, taskName: draggedTask.name })
        return
      }

      await commitMove(result)
    },
    [columns, setColumns, boardId, canFinalize, snackbar, commitMove],
  )

  const confirmPendingMove = useCallback(async () => {
    const pending = pendingMove
    setPendingMove(null)
    if (pending) await commitMove(pending.result)
  }, [pendingMove, commitMove])

  const cancelPendingMove = useCallback(() => setPendingMove(null), [])

  return { handleDragEnd, pendingMove, confirmPendingMove, cancelPendingMove }
}

export default useKanbanDragDrop
